import React from 'react';
import { fmt } from './utils/fmt';

function BarRow({ label, value, displayValue, maxVal, color, labelStyle }) {
  const pct = maxVal > 0 ? (value / maxVal) * 100 : 0;
  return (
    <div className="mb-3">
      <div className="flex justify-between items-baseline mb-1.5">
        <span className="text-[14px]" style={{ color: labelStyle || '#777888' }}>{label}</span>
        <span className="text-[16px] font-semibold" style={{ color: '#1F1F1F', fontVariantNumeric: 'tabular-nums' }}>{displayValue}</span>
      </div>
      <div className="rounded-full overflow-hidden" style={{ height: 12, background: '#E0E0DF' }}>
        <div className="h-full rounded-full" style={{ width: `${Math.min(pct, 100)}%`, background: color, transition: 'width 0.7s ease' }} />
      </div>
    </div>
  );
}

function MiniBarRow({ label, value, displayValue, maxVal, color }) {
  const pct = maxVal > 0 ? (value / maxVal) * 100 : 0;
  return (
    <div className="mb-2.5">
      <div className="flex justify-between items-baseline mb-1">
        <span className="text-[14px]" style={{ color: '#777888' }}>{label}</span>
        <span className="text-[14px] font-semibold" style={{ color: '#1F1F1F', fontVariantNumeric: 'tabular-nums' }}>{displayValue}</span>
      </div>
      <div className="rounded-full overflow-hidden" style={{ height: 12, background: '#E0E0DF' }}>
        <div className="h-full rounded-full" style={{ width: `${Math.min(pct, 100)}%`, background: color, transition: 'width 0.7s ease' }} />
      </div>
    </div>
  );
}

export default function MarketComparison({ baseline, market }) {
  const ppSqm = baseline?.ppSqm;
  const avg = market?.avg;
  if (!ppSqm) return null;

  const over = baseline?.marketOverMarket;
  const maxKaufpreis = Math.max(ppSqm, avg || 0) * 1.1;

  const mieteSqm = baseline?.monatskaltmiete && baseline?.wohnflaeche
    ? Math.round((baseline.monatskaltmiete / baseline.wohnflaeche) * 100) / 100
    : null;
  const marktMiete = market?.avgRent;
  const mietPotenzial = mieteSqm && marktMiete ? Math.round((marktMiete - mieteSqm) * 100) / 100 : null;
  const maxMiete = Math.max(mieteSqm || 0, marktMiete || 0) * 1.1;

  return (
    <div className="bg-white rounded-xl p-4" style={{ border: '1px solid #E0E0DF' }}>
      <h3 className="text-[20px] font-semibold text-[#1F1F1F] mb-3">Marktvergleich</h3>

      {/* ── Kaufpreis / m² ── */}
      <div className="text-[12px] font-semibold uppercase tracking-wider mb-2" style={{ color: '#8C8FA0', letterSpacing: '0.5px' }}>
        Kaufpreis / m²
      </div>

      {avg ? (
        <>
          <BarRow
            label="Dein Preis"
            value={ppSqm}
            displayValue={`${fmt(ppSqm)} €/m²`}
            maxVal={maxKaufpreis}
            color="#1DB3B3"
          />
          <BarRow
            label="Markt-Ø"
            value={avg}
            displayValue={`${fmt(Math.round(avg))} €/m²`}
            maxVal={maxKaufpreis}
            color="#ACACB8"
          />

          {over != null && (
            <div className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[14px] font-semibold mb-3 ${
              over <= 0 ? 'bg-[#E8F7E6] text-[#1FA534]' : over <= 10 ? 'bg-[#FCF7E6] text-[#F0C045]' : 'bg-[#FDEAEA] text-[#E83838]'
            }`}>
              {over <= 0 ? '✓' : '⚠'} {Math.abs(over)}% {over <= 0 ? 'unter' : 'über'} Markt
            </div>
          )}
        </>
      ) : (
        <>
          <div className="text-[18px] font-bold text-[#1F1F1F] mb-1" style={{ fontVariantNumeric: 'tabular-nums' }}>{fmt(ppSqm)} €/m²</div>
          <p className="text-[13px]" style={{ color: '#9A9AA0' }}>Marktdaten werden geladen…</p>
        </>
      )}

      {/* ── Mietmarkt / m² ── */}
      {(mieteSqm || marktMiete) && (
        <>
          <div className="border-t my-3" style={{ borderColor: '#E0E0DF' }} />
          <div className="text-[12px] font-semibold uppercase tracking-wider mb-2" style={{ color: '#8C8FA0', letterSpacing: '0.5px' }}>
            Mietmarkt / m²
          </div>

          {mieteSqm && (
            <MiniBarRow
              label="Aktuelle Miete"
              value={mieteSqm}
              displayValue={`${mieteSqm.toFixed(2).replace('.', ',')} €/m²`}
              maxVal={maxMiete}
              color="#1DB3B3"
            />
          )}
          {marktMiete && (
            <MiniBarRow
              label="Markt-Ø"
              value={marktMiete}
              displayValue={`${marktMiete.toFixed(2).replace('.', ',')} €/m²`}
              maxVal={maxMiete}
              color="#ACACB8"
            />
          )}
          {mietPotenzial != null && (
            <div className="flex justify-between items-center mt-1">
              <span className="text-[13px]" style={{ color: '#777888' }}>Potenzial</span>
              <span className="text-[15px] font-semibold" style={{ color: mietPotenzial > 0 ? '#1FA534' : '#9A9AA0', fontVariantNumeric: 'tabular-nums' }}>
                {mietPotenzial > 0 ? '+' : ''}{mietPotenzial.toFixed(2).replace('.', ',')} €/m²
              </span>
            </div>
          )}
        </>
      )}

      {/* ── Markt-Referenz ── */}
      <div className="border-t my-3" style={{ borderColor: '#E0E0DF' }} />
      <div className="text-[12px] font-semibold uppercase tracking-wider mb-2" style={{ color: '#8C8FA0', letterSpacing: '0.5px' }}>
        Markt-Referenz
      </div>

      {/* Dieses Objekt */}
      <div className="flex items-center justify-between px-2 py-1.5 rounded-md mb-1" style={{ background: '#E8F7F7', borderRadius: 6 }}>
        <span className="text-[14px] font-semibold" style={{ color: '#0E7A6B' }}>Dieses Objekt</span>
        <span className="text-[15px] font-semibold" style={{ color: '#0E7A6B', fontVariantNumeric: 'tabular-nums' }}>{fmt(ppSqm)} €/m²</span>
      </div>
      {avg && (
        <div className="flex items-center justify-between px-2 py-1.5">
          <span className="text-[14px]" style={{ color: '#8C8FA0' }}>Markt-Ø</span>
          <span className="text-[14px]" style={{ color: '#8C8FA0', fontVariantNumeric: 'tabular-nums' }}>{fmt(Math.round(avg))} €/m²</span>
        </div>
      )}
    </div>
  );
}
